import { useRef, ChangeEvent, useEffect, useCallback, memo } from "react";
import SearchIcon from "@Icons/SearchIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import RoadViewMap from "../RoadViewMap";
import styles from "./map.module.scss";
import useClickOutside from "hooks/useOutsideClick";
import { isEmptyObject } from "helpers/validations";
import DistanceStripDesktop from "@Pages/Search/components/DesktopMapSection/components/DistanceStripDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import { getFilteredUniversityFromLocal } from "../../utils/utils";
import { useMapState, useMapDispatch } from "../../context/MapContext";
//

// Import the University interface from utils
import type { University } from "../../utils/utils";
import wrapperHOC from "@Utils/wrapperHOC";
import CrossIcon from "@Icons/CrossIcon";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";

type MapProps = {
  isDesktop: boolean;
  inventory: any;
  onClick?: () => void;
  isMapLoaded?: boolean;
  onOptionClick?: (option: University, value: boolean) => void;
  insideModal?: boolean;
  showModal?: boolean;
};

const MapV3 = ({
  isDesktop = false,
  onClick,
  isMapLoaded = false,
  inventory,
  onOptionClick,
  insideModal = false,
  showModal,
}: MapProps) => {
  // Use separate context hooks for state and dispatch
  const state = useMapState();
  const dispatch = useMapDispatch();

  // Destructure only the state properties we need
  const {
    options,
    value,
    showOptions,
    universities,
    transportDataStrip: distanceObject,
    regionMarker,
    selectedUniversityFromLocal,
    showDistanceCard,
    showMiles,
  } = state;

  const { t } = useTranslationHook();
  const ref = useRef(null);
  const userLanguage = useSelector(({ appData }: AppState) => appData.userLanguage);

  const filteredOptionsFromLocal = getFilteredUniversityFromLocal(
    universities,
    selectedUniversityFromLocal,
  );

  // Set initial value from selected university (desktop only)
  useEffect(() => {
    if (isDesktop && filteredOptionsFromLocal) {
      dispatch({ type: "SET_VALUE", payload: filteredOptionsFromLocal.name });
    }
  }, [filteredOptionsFromLocal]);

  // Reset value when distance card is hidden
  useEffect(() => {
    if (!showDistanceCard) {
      dispatch({ type: "SET_VALUE", payload: "" });
      dispatch({ type: "SET_SELECTED_OPTION", payload: null });
    }
  }, [showDistanceCard]);

  // Handle modal display and initialization
  useEffect(() => {
    if (showModal && filteredOptionsFromLocal && onOptionClick) {
      onOptionClick(filteredOptionsFromLocal, true);
    }
  }, [showModal, filteredOptionsFromLocal, onOptionClick, dispatch]);

  // Memoize event handlers to prevent unnecessary re-renders
  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      dispatch({ type: "SET_VALUE", payload: inputValue });

      // Clear marker when input is empty
      if (inputValue === "") {
        dispatch({ type: "SET_SHOW_DISTANCE_CARD", payload: false });
        dispatch({ type: "SET_SELECTED_OPTION", payload: null });
        dispatch({ type: "SET_OPTIONS", payload: universities });
        dispatch({ type: "SET_SHOW_OPTIONS", payload: true });
        return;
      }

      try {
        dispatch({ type: "SET_SHOW_OPTIONS", payload: true });
        // Filter universities based on input
        const filteredOptions = inputValue
          ? universities.filter((uni: University) =>
              uni?.name?.toLowerCase()?.includes(inputValue.toLowerCase()),
            )
          : [];

        // Show fallback text if university not present
        dispatch({
          type: "SET_OPTIONS",
          payload: filteredOptions.length === 0 ? [] : filteredOptions,
        });
      } catch (err) {
        dispatch({ type: "SET_OPTIONS", payload: universities });
      }
    },
    [universities, regionMarker, dispatch],
  );

  const handleOptionSelect = useCallback(
    (option: University) => {
      if (!option) return;

      dispatch({ type: "SET_SHOW_OPTIONS", payload: false });
      if (onOptionClick) {
        onOptionClick(option, true);
      }
      dispatch({ type: "SET_VALUE", payload: option.name || "" });
    },
    [onOptionClick, dispatch],
  );

  // Clear search input and reset options/state
  const handleClearSearch = useCallback(() => {
    dispatch({ type: "SET_VALUE", payload: "" });
    dispatch({ type: "SET_SELECTED_OPTION", payload: null });
    dispatch({ type: "SET_OPTIONS", payload: universities });
    dispatch({ type: "SET_SHOW_OPTIONS", payload: true });
    dispatch({ type: "SET_SHOW_DISTANCE_CARD", payload: false });
  }, [universities, dispatch]);

  // Close options dropdown when clicking outside
  useClickOutside(ref, () => dispatch({ type: "SET_SHOW_OPTIONS", payload: false }));

  const renderDistanceStrip = useCallback(() => {
    if (isEmptyObject(distanceObject) || !showDistanceCard) return null;

    return (
      <div
        className={formatClassNames(
          styles.distanceContainer,
          userLanguage === "de" && styles.distanceContainerDe,
        )}
      >
        <DistanceStripDesktop
          displayText={
            isDesktop ? t("Distance from your Accommodation") : t("index.distance_from_the")
          }
          selectedDistanceObj={distanceObject}
          setShowDistanceCard={(showCard: boolean) =>
            dispatch({ type: "SET_SHOW_DISTANCE_CARD", payload: showCard })
          }
          showMiles={showMiles}
          t={t}
        />
      </div>
    );
  }, [distanceObject, showDistanceCard, showMiles, userLanguage]);

  return (
    <>
      <div
        className={formatClassNames(
          styles.mapContainer,
          isDesktop && styles.desktop,
          insideModal && styles.insideModal,
          insideModal && userLanguage === "de" && styles.insideModalDe,
          userLanguage === "de" && styles.mapContainerDeCtrl,
        )}
      >
        <RoadViewMap data={inventory} isMapLoaded={isMapLoaded} />

        {!isDesktop && insideModal && renderDistanceStrip()}

        {!isDesktop && !insideModal && (
          <button type="button" className={styles.floatingButton} onClick={onClick}>
            {t("propertypage.components.mapsectionv2.explore")}
          </button>
        )}

        {isDesktop && (
          <>
            <div className={styles.searchInputDesktopContainer} ref={ref}>
              <div
                className={styles.searchInput}
                onClick={() => dispatch({ type: "SET_SHOW_OPTIONS", payload: true })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && dispatch({ type: "SET_SHOW_OPTIONS", payload: true })
                }
              >
                <SearchIcon fill="#6B7280" />
                <input
                  className={styles.searchInputDesktop}
                  type="search"
                  placeholder={t(
                    "propertypage.components.mapsectionv2.searchforuniversitiesplaceholder",
                  )}
                  value={value}
                  onChange={handleOnChange}
                  aria-label={t(
                    "propertypage.components.mapsectionv2.searchforuniversitiesplaceholder",
                  )}
                />
              </div>

              {showOptions && options?.length >= 0 && (
                <div className={styles.optionsDesktop} role="listbox">
                  {options.length > 0 ? (
                    options.map((option: University) => (
                      <div
                        className={styles.optionDesktop}
                        key={option?.name + option?.id}
                        onClick={() => handleOptionSelect(option)}
                        onKeyDown={(e) => e.key === "Enter" && handleOptionSelect(option)}
                        role="option"
                        tabIndex={0}
                        aria-selected={option.name === value}
                      >
                        {option?.name}
                      </div>
                    ))
                  ) : (
                    <div className={styles.optionDesktop} key="No matches found in this city">
                      No matches found in this city
                    </div>
                  )}
                </div>
              )}
            </div>

            {renderDistanceStrip()}
          </>
        )}
      </div>
      {showModal && (
        <>
          <div className={styles.searchMobileContainer} ref={ref}>
            <div className={styles.searchInputMobileContainer}>
              <SearchIcon fill="#6B7280" />
              <input
                type="text"
                placeholder={t(
                  "propertypage.components.mapsectionv2.searchforuniversitiesplaceholder",
                )}
                className={styles.searchInputMobile}
                value={value}
                onChange={handleOnChange}
                onFocus={() => {
                  dispatch({ type: "SET_SHOW_OPTIONS", payload: true });
                  dispatch({ type: "SET_OPTIONS", payload: universities });
                }}
              />
              {value && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSearch();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleClearSearch();
                  }}
                  aria-label={t("Clear search")}
                  className={styles.clearButtonMobile}
                >
                  <CrossIcon />
                </div>
              )}
            </div>
            {showOptions && options?.length >= 0 && (
              <div className={styles.optionsMobile_mini_program}>
                {options?.length > 0 ? (
                  options.map((option: any) => (
                    <div
                      role="none"
                      className={styles.optionMobile}
                      key={option?.name + option?.id}
                      onClick={() => handleOptionSelect(option)}
                    >
                      {option?.name}
                    </div>
                  ))
                ) : (
                  <div role="none" className={styles.optionMobile} key="default">
                    No matches found in this city
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default wrapperHOC(memo(MapV3), {
  componentName: "PropertyPage-MapV3",
  showForChina: true,
});
